import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { getToken, useMsalAuth } from "../../authConfig";
import { getHeaders } from "../../api/api";
import { Button, Modal } from "../../components/ui";
import { ibicPreviewSchema, selectedIbicCode } from "./ibicModel";
import type { IbicEditorConfig, IbicPreview } from "./ibicModel";
import { IbicCodePicker } from "./IbicCodePicker";
import { WorkflowChoiceResult } from "./WorkflowResultPresentation";

type Props = { config: IbicEditorConfig; content: string; disabled: boolean; onChange: (content: string) => void };
const backend = (import.meta.env.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

/** A selection is only applied after its explanation has been reviewed. */
export function IbicCodeEditor({ config, content, disabled, onChange }: Props) {
    const { t } = useTranslation();
    const { instance } = useMsal();
    const text = (key: string, fallback: string) => t(`documentFlow.ibic.${key}`, { defaultValue: fallback });
    const [selection, setSelection] = useState<string | null>(null);
    const [preview, setPreview] = useState<IbicPreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const request = useRef<AbortController | null>(null);
    const current = selectedIbicCode(content, config.options);
    const identity = JSON.stringify([config.project_id, config.draft_id, config.instance_id, config.node_id, config.expected_revision, content]);
    useEffect(() => {
        request.current?.abort();
        setSelection(null);
        setPreview(null);
        setError(null);
        setBusy(false);
        return () => request.current?.abort();
    }, [identity, disabled]);
    const close = () => {
        request.current?.abort();
        setSelection(null);
        setPreview(null);
        setError(null);
        setBusy(false);
    };
    const generate = async (code: string) => {
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        setSelection(code);
        setPreview(null);
        setError(null);
        setBusy(true);
        try {
            const token = useMsalAuth ? await getToken(instance) : undefined;
            if (controller.signal.aborted) return;
            const parts = [config.project_id, config.draft_id, config.instance_id, config.node_id].map(encodeURIComponent);
            const response = await fetch(`${backend}/projects/${parts[0]}/documentation-drafts/${parts[1]}/boxes/${parts[2]}/${parts[3]}/ibic-preview`, {
                method: "POST",
                signal: controller.signal,
                headers: { ...(await getHeaders(token)), "Content-Type": "application/json" },
                body: JSON.stringify({ code, expected_revision: config.expected_revision })
            });
            if (!response.ok) throw new Error(text("failed", "Unable to generate an explanation. Refresh if this card has changed, or try again."));
            const result = ibicPreviewSchema.parse(await response.json());
            if (result.code !== code) throw new Error(text("failed", "Unable to generate an explanation. Try again."));
            if (!controller.signal.aborted) setPreview(result);
        } catch (caught: unknown) {
            if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : text("failed", "Unable to generate an explanation."));
        } finally {
            if (!controller.signal.aborted) setBusy(false);
        }
    };
    return (
        <WorkflowChoiceResult
            picker={<IbicCodePicker options={config.options} value={selection ?? current} disabled={disabled} onSelect={code => void generate(code)} />}
            content={content}
        >
            <Modal
                open={selection !== null}
                size="md"
                onOpenChange={open => {
                    if (!open) close();
                }}
            >
                <Modal.Header title={text("review", "Review the new IBIC explanation")} />
                <Modal.Body>
                    <p>{text("notice", "Confirm the code and explanation to apply them to your edit. Save the card afterwards.")}</p>
                    {busy && <p role="status">{text("generating", "Generating explanation…")}</p>}
                    {error && <p role="alert">{error}</p>}
                    {preview && <p style={{ whiteSpace: "pre-wrap" }}>{preview.content}</p>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={close}>
                        {text("cancel", "Cancel")}
                    </Button>
                    {error && selection && <Button onClick={() => void generate(selection)}>{text("retry", "Try again")}</Button>}
                    <Button
                        disabled={disabled || busy || !preview}
                        onClick={() => {
                            if (preview && !disabled) {
                                onChange(preview.content);
                                close();
                            }
                        }}
                    >
                        {text("confirm", "Confirm selection")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </WorkflowChoiceResult>
    );
}
